import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';

export function YouTubeFilmCard({ film, i = 0, testidPrefix = 'film' }) {
  const [active, setActive] = useState(false);

  return (
    <motion.article
      data-testid={`${testidPrefix}-${i}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <div className="relative w-full bg-black border border-white/10 overflow-hidden">
        <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
          {active ? (
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube-nocookie.com/embed/${film.id}?autoplay=1&rel=0&modestbranding=1`}
              title={film.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : (
            <button
              data-testid={`${testidPrefix}-play-${i}`}
              onClick={() => setActive(true)}
              aria-label={`Play ${film.title}`}
              className="absolute inset-0 h-full w-full"
            >
              <img
                src={`https://i.ytimg.com/vi/${film.id}/maxresdefault.jpg`}
                onError={(e) => {
                  e.currentTarget.src = `https://i.ytimg.com/vi/${film.id}/hqdefault.jpg`;
                }}
                alt={film.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-full bg-[#D8FF3A]/90 text-black transition-transform duration-300 group-hover:scale-110">
                  <Play size={26} className="ml-1" fill="currentColor" />
                </span>
              </span>
              {film.subtitle && (
                <span className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/80 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/15">
                  <span className="h-1.5 w-1.5 bg-[#D8FF3A] rounded-full" />
                  {film.subtitle}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="pt-5">
        <h3
          className="font-display text-white font-bold tracking-tight"
          style={{ fontSize: 'clamp(22px, 2.4vw, 34px)', lineHeight: 1.05 }}
        >
          {film.title}
        </h3>
        {film.description && (
          <p className="mt-3 text-sm md:text-base text-white/60 leading-relaxed max-w-xl">
            {film.description}
          </p>
        )}
      </div>
    </motion.article>
  );
}

export default function YouTubeFilmGrid({ items, testidPrefix = 'film' }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
      {items.map((f, i) => (
        <YouTubeFilmCard key={f.id} film={f} i={i} testidPrefix={testidPrefix} />
      ))}
    </div>
  );
}
